import {
    Constraint_Types_Enum,
    GetRelatedScenarioResultsQuery,
    GetRemainingScenarioResultsQuery,
    GetScenarioResultsByProjectIdQuery
} from "generated/graphql";
import { convertMaybeToUndefined } from "graphql/helpers/queryHelpers";
import {
    readAdditionalColumnValue,
    RowWithMetadata
} from "in_queue/utils/additionalColumnsMetadata";
import {
    InQueueRegionConfig,
    RelatedProjectsTypeEnum
} from "./configPerRegion";
import { Project } from "./projectType";

export const ALLOCATED_COST_WITHOUT_SINCS_KEY =
    "sum_allocated_cost_without_sincs";

export interface ScenarioResults {
    constraints: ScenarioConstraint[];
    allocatedUpgrades: ScenarioAllocatedUpgrade[];
    projects: ScenarioProject[];
    relatedProjectIds: Set<string>;
}

// Re-exports the Hasura-generated status types under a different name
// This enables us to change how this is structured in the future if we
// want to decouple from the Hasura typings.
export type ConstraintType = Constraint_Types_Enum;
export const ConstraintType = Constraint_Types_Enum;

export interface ScenarioConstraint {
    constraintType: ConstraintType;
    monitoredFacilityName: string;
    worstContingencyName?: string;
    preStudyLoad?: number;
    postStudyLoad?: number;
    preStudyFlow?: number;
    postStudyFlow?: string;
    preStudyRating?: number;
    postStudyRating?: number;
    networkUpgrades: string[];
    projectId: string;
    dfax: number;
    mwImpact: number;
    notes?: string;
    model?: string;
    area?: string;
    metadata: { [key: string]: string };

    // These fields are not filled in everywhere yet
    elementType?: string;
    lineLength?: number;
    voltage?: string;
    // The facility details is a summary of the three fields above
    facilityDetails?: string;
}

// Prior-queue (PQ) identification: PQ rows carry is_prior_queue + study_cycle in their
// scenario_results metadata (set by the iq_miso service).
export const isPriorQueueConstraint = (
    c: Pick<ScenarioConstraint, "metadata">
): boolean => {
    const v = (c.metadata as Record<string, unknown> | undefined)?.[
        "is_prior_queue"
    ];
    return v === true || v === "true";
};

export const getConstraintStudyCycle = (
    c: Pick<ScenarioConstraint, "metadata">
): string | undefined => {
    const v = (c.metadata as Record<string, unknown> | undefined)?.[
        "study_cycle"
    ];
    return typeof v === "string" && v.length > 0 ? v : undefined;
};

// PJM stamps each cost-allocated constraint row with the Manual 14H criterion it
// met: "Individual Criteria: ...", "Aggregate Criteria: ...",
// "Potential Aggregate Criteria: ..." or "Contingent Criteria Met". Written by
// the pipeline's `get_cost_allocation_criteria`; prior-queue rows carry none.
export const COST_ALLOCATION_CRITERIA_COLUMN_KEY = "cost_allocation_criteria";
export const POTENTIAL_AGGREGATE_CRITERIA_PREFIX =
    "Potential Aggregate Criteria";

export const getCostAllocationCriteria = (
    c: RowWithMetadata
): string | undefined => {
    const v = readAdditionalColumnValue(
        c.metadata,
        COST_ALLOCATION_CRITERIA_COLUMN_KEY
    );
    return typeof v === "string" && v.length > 0 ? v : undefined;
};

// A potential aggregate contributor overloads the facility but is not
// cost-allocated for it. The pipeline appends these rows with $0
// (`merge_potential_costs`) so they appear in the constraints table without
// paying for the upgrade.
export const isPotentialAggregateConstraint = (c: RowWithMetadata): boolean => {
    const criteria = getCostAllocationCriteria(c);
    return (
        criteria !== undefined &&
        criteria.startsWith(POTENTIAL_AGGREGATE_CRITERIA_PREFIX)
    );
};

export interface ScenarioAllocatedUpgrade {
    constraintType: ConstraintType;
    networkUpgradeId: string;
    networkUpgradeName: string;
    projectId: string;
    allocatedCost: number;
    maxDfax: number;
    sumMwImpact: number;
    totalCost: number;
    monitoredFacilityNames: string[];
    notes?: string;
    percentAllocation: number;
    metadata: { [key: string]: string };
}
export interface ScenarioProject {
    projectId: string;
    erisSizeMw: number;
    nrisSizeMw: number;
    costPerMw: number | undefined;
    sumAllocatedCost: number;
    assignedUpgrades: string[];
    overloadedFacilities: string[];
    sumAllocatedCostByConstraintType: { [constraintType: string]: number };
    metadata: { [key: string]: string };
    /**
     * Rank by $/MW in this scenario's pool, written by the pipeline.
     * Undefined for a project the scenario did not rank.
     */
    rank: number | undefined;
    /** P1..P100 for {@link rank}. */
    rankPercentile: number | undefined;
    /** Size of the pool {@link rank} was taken in. */
    totalRanked: number | undefined;

    // Derived field
    numSharedUpgrades: number;
}

type HasuraQueryResult = GetScenarioResultsByProjectIdQuery["scenarios"][0];
type HasuraConstraint = HasuraQueryResult["scenario_constraints"][0];
type HasuraAllocatedUpgrade =
    HasuraQueryResult["scenario_allocated_upgrades"][0];
type HasuraScenarioProject = HasuraQueryResult["scenario_projects"][0];

export const getRelatedProjectIds = ({
    relatedProjectsType,
    projectId,
    scenarioProjects,
    scenarioConstraints
}: {
    relatedProjectsType: RelatedProjectsTypeEnum;
    projectId: string;
    scenarioProjects: ScenarioProject[];
    scenarioConstraints: ScenarioConstraint[];
}): string[] => {
    let relatedProjectIds: string[] = [];
    if (relatedProjectsType == RelatedProjectsTypeEnum.SharedAllocations) {
        relatedProjectIds = scenarioProjects
            .filter((sp) => sp.numSharedUpgrades > 0)
            .map((sp) => sp.projectId);
    } else if (
        relatedProjectsType == RelatedProjectsTypeEnum.SharedMonitoredFacilities
    ) {
        // For PJM TC2 Ph1 users are able to include TC1 projects in their project size assumptions, so our constraints output includes TC1 projects. However,
        // since TC1 projects are not cost-allocated for these constraints, we don't include them in the allocations and projects results. This unfortunately
        // doesn't work well with our "Related projects" logic as we only consider projects to be related if they share upgrades. Since TC1 projects don't get
        // cost-allocated, they will never share upgrades with TC2 projects and therefore never get shown when the Related projects filter is toggled. To fix
        // this, we're changing the Related projects logic specifically for PJM to consider a project as "related" if they both overload the same monitored
        // facility.
        //
        // Potential aggregate contributors overload the facility but pay nothing for it. Another project's potential row never makes it related:
        // a paying project does not need to know who else overloads the line without paying. The current project's own potential rows do
        // count, though: a project that pays nothing on a line still needs to see who does pay for it. Prior-queue rows carry no criteria
        // and stay in.
        const relevantMonitoredFacilities = new Set(
            scenarioConstraints
                .filter((sc) => sc.projectId === projectId)
                .map((sc) => sc.monitoredFacilityName)
        );

        relatedProjectIds = scenarioConstraints
            .filter(
                (sc) =>
                    !isPotentialAggregateConstraint(sc) &&
                    relevantMonitoredFacilities.has(sc.monitoredFacilityName)
            )
            .map((sc) => sc.projectId);
    } else {
        console.warn(`Invalid related projects type: ${relatedProjectsType}`);
    }

    return relatedProjectIds;
};

export const convertHasuraScenarioDataToScenarioResults = (
    hasuraResult: GetScenarioResultsByProjectIdQuery,
    currentProject: Pick<Project, "projectId">,
    regionConfig: InQueueRegionConfig
): ScenarioResults => {
    if (hasuraResult.scenarios.length !== 1) {
        throw new Error("Couldn't find single scenario result for id");
    }
    const scenario = hasuraResult.scenarios[0];
    const scenarioProjects = convertHasuraScenarioProjectsToScenarioProjects(
        scenario.scenario_projects,
        currentProject
    );

    const allocatedUpgrades = convertHasuraAllocatedUpgradesToUpgrades(
        scenario.scenario_allocated_upgrades
    );

    const scenarioConstraints = convertHasuraConstraintsToConstraints(
        scenario.scenario_constraints
    );

    const projects =
        regionConfig.relatedProjectsType ===
        RelatedProjectsTypeEnum.SharedMonitoredFacilities
            ? withSharedPaidUpgradeCounts({
                  projects: scenarioProjects,
                  allocatedUpgrades,
                  currentProjectId: currentProject.projectId,
                  otherAllocationsLoaded: false
              })
            : scenarioProjects;

    const relatedProjectIds = getRelatedProjectIds({
        relatedProjectsType: regionConfig.relatedProjectsType,
        projectId: currentProject.projectId,
        scenarioProjects: projects,
        scenarioConstraints
    });

    return {
        constraints: scenarioConstraints,
        allocatedUpgrades,
        projects,
        relatedProjectIds: new Set([
            ...relatedProjectIds,
            currentProject.projectId
        ])
    };
};

export const convertHasuraScenarioDataToScenarioResultsWithExistingData = ({
    hasuraResult,
    existingScenarioResults,
    currentProject,
    regionConfig
}: {
    hasuraResult:
        | GetRelatedScenarioResultsQuery
        | GetRemainingScenarioResultsQuery;
    existingScenarioResults: ScenarioResults;
    currentProject: Pick<Project, "projectId">;
    regionConfig: InQueueRegionConfig;
}): ScenarioResults => {
    if (hasuraResult.scenarios.length !== 1) {
        throw new Error("Couldn't find single scenario result for id");
    }
    const scenario = hasuraResult.scenarios[0];

    const allocatedUpgrades = [
        ...convertHasuraAllocatedUpgradesToUpgrades(
            scenario.scenario_allocated_upgrades
        ),
        ...existingScenarioResults.allocatedUpgrades
    ];

    const scenarioConstraints = [
        ...convertHasuraConstraintsToConstraints(scenario.scenario_constraints),
        ...existingScenarioResults.constraints
    ];

    const projects =
        regionConfig.relatedProjectsType ===
        RelatedProjectsTypeEnum.SharedMonitoredFacilities
            ? withSharedPaidUpgradeCounts({
                  projects: existingScenarioResults.projects,
                  allocatedUpgrades,
                  currentProjectId: currentProject.projectId,
                  otherAllocationsLoaded: true
              })
            : existingScenarioResults.projects;

    const relatedProjectIds = getRelatedProjectIds({
        relatedProjectsType: regionConfig.relatedProjectsType,
        projectId: currentProject.projectId,
        scenarioProjects: projects,
        scenarioConstraints
    });

    return {
        constraints: scenarioConstraints,
        allocatedUpgrades,
        projects,
        relatedProjectIds: new Set([
            ...relatedProjectIds,
            currentProject.projectId
        ])
    };
};

// PJM: "# shared upgrades" counts only the upgrades both projects pay for. A potential aggregate
// or contingent row is a $0 allocation, so `assigned_upgrades`, which lists every upgrade a
// project has a row on, overstates the count in both directions: the current project's own
// unpaid upgrades and the other project's unpaid upgrades. Paying means allocated cost above
// zero.
//
// The other projects' allocations arrive in the second fetch. Until then a project without
// allocation rows falls back to its assigned list, which can only overstate. Once loaded, a
// project with no allocation rows on the current project's upgrades shares nothing.
export const withSharedPaidUpgradeCounts = ({
    projects,
    allocatedUpgrades,
    currentProjectId,
    otherAllocationsLoaded
}: {
    projects: ScenarioProject[];
    allocatedUpgrades: ScenarioAllocatedUpgrade[];
    currentProjectId: string;
    otherAllocationsLoaded: boolean;
}): ScenarioProject[] => {
    const paidByProject = new Map<string, Set<string>>();
    for (const upgrade of allocatedUpgrades) {
        const paid = paidByProject.get(upgrade.projectId) ?? new Set<string>();
        if (upgrade.allocatedCost > 0) {
            paid.add(upgrade.networkUpgradeName);
        }
        paidByProject.set(upgrade.projectId, paid);
    }
    const currentPaid =
        paidByProject.get(currentProjectId) ?? new Set<string>();

    return projects.map((project) => {
        const fetched = paidByProject.get(project.projectId);
        let theirUpgrades: ReadonlySet<string>;
        if (fetched !== undefined) {
            theirUpgrades = fetched;
        } else if (otherAllocationsLoaded) {
            theirUpgrades = new Set<string>();
        } else {
            theirUpgrades = new Set(project.assignedUpgrades);
        }
        let numSharedUpgrades = 0;
        theirUpgrades.forEach((name) => {
            if (currentPaid.has(name)) {
                numSharedUpgrades += 1;
            }
        });
        return { ...project, numSharedUpgrades };
    });
};

const convertHasuraConstraintsToConstraints = (
    hasuraConstraints: HasuraConstraint[]
): ScenarioConstraint[] => {
    return hasuraConstraints.map((c) => {
        let facilityDetails;
        if (c.element_type) {
            if (c.element_type === "transformer" && c.voltage) {
                facilityDetails = `${c.voltage}kV transformer`;
            } else if (c.element_type === "line or bus section") {
                const lineLength = c.line_length
                    ? `${c.line_length?.toFixed(2)}-mile `
                    : "";
                const voltage = c.voltage ? `${c.voltage}kV ` : "";
                if (lineLength || voltage) {
                    facilityDetails = `${lineLength}${voltage}line`;
                }
            }
        }

        return {
            ...hasuraConstraintToConstraint(c),
            facilityDetails
        };
    });
};

const hasuraConstraintToConstraint = (
    c: HasuraConstraint
): ScenarioConstraint => {
    return {
        constraintType: c.constraint_type,
        monitoredFacilityName: c.monitored_facility_name,
        elementType: c.element_type ?? undefined,
        lineLength: c.line_length ?? undefined,
        voltage: c.voltage ?? undefined,
        worstContingencyName: c.worst_contingency_name ?? undefined,
        preStudyLoad: c.pre_study_load ?? undefined,
        postStudyLoad: c.post_study_load ?? undefined,
        preStudyFlow: c.pre_study_flow ?? undefined,
        postStudyFlow: c.post_study_flow ?? undefined,
        preStudyRating: c.pre_study_rating ?? undefined,
        postStudyRating: c.post_study_rating ?? undefined,
        networkUpgrades: c.network_upgrade_names,
        projectId: c.project_id,
        dfax: c.dfax,
        mwImpact: c.mw_impact,
        notes: convertMaybeToUndefined(c.notes),
        model: convertMaybeToUndefined(c.model),
        area: c.area ?? undefined,
        metadata: c.metadata ?? {}
    };
};

const convertHasuraPercentAllocation = (u: HasuraAllocatedUpgrade): number => {
    let percentAllocation = 0.0;
    if (u.percent_allocation) {
        percentAllocation = u.percent_allocation;
    } else if (u.total_cost !== 0 && u.allocated_cost !== 0) {
        // TODO: remove this fallback once the percent_allocation field is no longer nullable
        percentAllocation = u.allocated_cost / u.total_cost;
    }

    return percentAllocation;
};
const convertHasuraAllocatedUpgradesToUpgrades = (
    hasuraUpgrades: HasuraAllocatedUpgrade[]
): ScenarioAllocatedUpgrade[] => {
    return hasuraUpgrades.map((u) => ({
        constraintType: u.constraint_type,
        networkUpgradeId: u.network_upgrade_id,
        networkUpgradeName: u.network_upgrade_name,
        projectId: u.project_id,
        allocatedCost: u.allocated_cost,
        maxDfax: u.max_dfax,
        sumMwImpact: u.sum_mw_impact,
        totalCost: u.total_cost,
        percentAllocation: convertHasuraPercentAllocation(u),
        monitoredFacilityNames: u.monitored_facility_names,
        notes: convertMaybeToUndefined(u.notes),
        metadata: u.metadata ?? {}
    }));
};

type ConvertedHasuraProject = Omit<ScenarioProject, "numSharedUpgrades">;

const convertHasuraScenarioProjectsToScenarioProjects = (
    hasuraProjects: HasuraScenarioProject[],
    currentProject: Pick<Project, "projectId">
): ScenarioProject[] => {
    let currentProjectUpgrades: Set<string> = new Set();
    const convertedProjects: ConvertedHasuraProject[] = [];
    for (const p of hasuraProjects) {
        if (p.project_id === currentProject.projectId) {
            currentProjectUpgrades = new Set(p.assigned_upgrades);
        }
        convertedProjects.push({
            projectId: p.project_id,
            erisSizeMw: p.eris_size_mw,
            nrisSizeMw: p.nris_size_mw,
            costPerMw: Number.isFinite(p.cost_per_mw)
                ? p.cost_per_mw
                : undefined,
            sumAllocatedCost: p.sum_allocated_cost,
            assignedUpgrades: p.assigned_upgrades,
            overloadedFacilities: p.overloaded_facilities,
            sumAllocatedCostByConstraintType:
                p.sum_allocated_cost_by_constraint_type ?? {},
            metadata: p.metadata ?? {},
            rank: p.ranks?.rank,
            rankPercentile: p.ranks?.percentile,
            totalRanked: p.ranks?.total_ranked
        });
    }

    const results = convertedProjects.map((p) => {
        const numSharedUpgrades = p.assignedUpgrades.filter((upgrade) =>
            currentProjectUpgrades.has(upgrade)
        ).length;

        return {
            ...p,
            numSharedUpgrades
        };
    });

    return results;
};
